"use client";

import { useEffect, useState, useCallback } from "react";
import { useFilesStore } from "@/stores/files-store";
import { apiRequest } from "@/lib/api";

const ONBOARDING_KEY = "wiki_arsenal_onboarding_done";

export interface OnboardingState {
  shouldShow: boolean;
  hasProjects: boolean;
  hasMembers: boolean;
  hasDocuments: boolean;
  dismissed: boolean;
  completedSteps: number[];
  markStepDone: (step: number) => void;
  dismiss: () => void;
  reset: () => void;
}

export function useOnboarding(): OnboardingState {
  const filesStore = useFilesStore();
  const [hasMembers, setHasMembers] = useState(false);
  const [completedSteps, setCompletedSteps] = useState<number[]>([]);
  const [dismissed, setDismissed] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    const done = localStorage.getItem(ONBOARDING_KEY) === "1";
    setDismissed(done);

    (async () => {
      try {
        const users = await apiRequest<{ items: { id: string }[] }>("/v1/users");
        const list = users?.items || users || [];
        setHasMembers(Array.isArray(list) && list.length >= 2);
      } catch {
        setHasMembers(false);
      } finally {
        setLoaded(true);
      }
    })();
  }, []);

  const hasProjects = filesStore.spaces.length > 0;
  const hasDocuments = filesStore.files.some((f) => f.type === "file");
  const shouldShow = loaded && !dismissed && (!hasProjects || !hasDocuments);

  const markStepDone = useCallback((step: number) => {
    setCompletedSteps((prev) => (prev.includes(step) ? prev : [...prev, step]));
  }, []);

  const dismiss = useCallback(() => {
    localStorage.setItem(ONBOARDING_KEY, "1");
    setDismissed(true);
  }, []);

  const reset = useCallback(() => {
    localStorage.removeItem(ONBOARDING_KEY);
    setDismissed(false);
    setCompletedSteps([]);
  }, []);

  return {
    shouldShow,
    hasProjects,
    hasMembers,
    hasDocuments,
    dismissed,
    completedSteps,
    markStepDone,
    dismiss,
    reset,
  };
}